import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SERVICES_CATALOG, getAllServices, calculatePackagePrice } from '../data/servicesCatalog';
import { apiRequest } from '../lib/api';
import {
  Package, Upload, Mic, MicOff, FileText, ChevronRight, ChevronDown, Check, X,
  DollarSign, Sparkles, Copy, Download, Brain, Building2, Target, Clock,
  Users, Rocket, Settings, Zap, ArrowRight, Loader2, CheckCircle2, AlertCircle,
  Square, Play, Pause, Trash2, Save, RefreshCw
} from 'lucide-react';

// Questions to understand client needs
const CLIENT_QUESTIONS = {
  es: [
    { id: 'business_type', question: '¿Qué tipo de negocio tienes?', placeholder: 'Ej: Agencia de marketing, Restaurante, E-commerce...', category: 'context' },
    { id: 'business_size', question: '¿Cuántos empleados tienen?', placeholder: 'Ej: Solo yo, 5-10, 20+...', category: 'context' },
    { id: 'current_tech', question: '¿Qué herramientas/sistemas usan actualmente?', placeholder: 'Ej: Excel, Google Sheets, Notion, ninguno...', category: 'context' },
    { id: 'main_problem', question: '¿Cuál es el problema principal que quieres resolver?', placeholder: 'Describe el mayor dolor de tu negocio...', category: 'needs', multiline: true },
    { id: 'goals', question: '¿Qué resultados esperas lograr?', placeholder: 'Ej: Automatizar procesos, conseguir más clientes, mejorar eficiencia...', category: 'needs', multiline: true },
    { id: 'timeline', question: '¿Cuál es tu timeline ideal?', placeholder: 'Ej: Lo antes posible, 1-2 meses, sin prisa...', category: 'logistics' },
    { id: 'budget_range', question: '¿Tienes un rango de presupuesto en mente?', placeholder: 'Ej: $500-1000, $2000-5000, flexible...', category: 'logistics' },
    { id: 'priorities', question: '¿Qué es lo más importante para ti?', placeholder: 'Ej: Calidad, velocidad, precio, soporte continuo...', category: 'preferences' },
    { id: 'previous_attempts', question: '¿Has intentado soluciones similares antes?', placeholder: 'Cuéntame qué funcionó y qué no...', category: 'context', multiline: true },
    { id: 'specific_features', question: '¿Hay funcionalidades específicas que necesitas?', placeholder: 'Ej: Login de usuarios, pagos online, reportes...', category: 'needs', multiline: true },
  ],
  en: [
    { id: 'business_type', question: 'What type of business do you have?', placeholder: 'E.g.: Marketing agency, Restaurant, E-commerce...', category: 'context' },
    { id: 'business_size', question: 'How many employees do you have?', placeholder: 'E.g.: Just me, 5-10, 20+...', category: 'context' },
    { id: 'current_tech', question: 'What tools/systems do you currently use?', placeholder: 'E.g.: Excel, Google Sheets, Notion, none...', category: 'context' },
    { id: 'main_problem', question: 'What is the main problem you want to solve?', placeholder: 'Describe your biggest business pain point...', category: 'needs', multiline: true },
    { id: 'goals', question: 'What results do you expect to achieve?', placeholder: 'E.g.: Automate processes, get more clients, improve efficiency...', category: 'needs', multiline: true },
    { id: 'timeline', question: 'What is your ideal timeline?', placeholder: 'E.g.: ASAP, 1-2 months, no rush...', category: 'logistics' },
    { id: 'budget_range', question: 'Do you have a budget range in mind?', placeholder: 'E.g.: $500-1000, $2000-5000, flexible...', category: 'logistics' },
    { id: 'priorities', question: 'What is most important to you?', placeholder: 'E.g.: Quality, speed, price, ongoing support...', category: 'preferences' },
    { id: 'previous_attempts', question: 'Have you tried similar solutions before?', placeholder: 'Tell me what worked and what didn\'t...', category: 'context', multiline: true },
    { id: 'specific_features', question: 'Are there specific features you need?', placeholder: 'E.g.: User login, online payments, reports...', category: 'needs', multiline: true },
  ]
};

const CATEGORY_ICONS = {
  context: Building2,
  needs: Target,
  logistics: Clock,
  preferences: Settings,
};

const LABELS = {
  es: {
    title: 'Construye Tu Paquete Personalizado',
    subtitle: 'Cuéntame sobre tu negocio y crearé el paquete perfecto para ti',
    uploadAudio: 'Subir Audio/Transcripción',
    uploadHint: 'Sube un archivo de audio o texto con tus respuestas y la IA las procesará automáticamente',
    recordAudio: 'Grabar Audio',
    recording: 'Grabando...',
    stopRecording: 'Detener',
    startRecording: 'Iniciar Grabación',
    processing: 'Procesando con IA...',
    transcribing: 'Transcribiendo audio...',
    analyzeButton: 'Analizar y Llenar',
    questionsTitle: 'Cuestionario de Necesidades',
    questionsHint: 'Responde las preguntas o sube/graba un audio para llenarlas automáticamente',
    recommendedServices: 'Servicios Recomendados',
    basedOnAnswers: 'Basado en tus respuestas, estos servicios son ideales para ti:',
    selectedServices: 'Servicios Seleccionados',
    totalInvestment: 'Inversión Total Estimada',
    oneTime: 'Único',
    monthly: 'Mensual',
    generatePrompt: 'Generar Master Prompt',
    promptTitle: 'Master Prompt para Construir',
    promptHint: 'Usa este prompt para comenzar a construir tu proyecto',
    copyPrompt: 'Copiar Prompt',
    copied: '¡Copiado!',
    downloadPrompt: 'Descargar',
    savePackage: 'Guardar Paquete',
    saved: '¡Guardado!',
    nextStep: 'Siguiente Paso',
    prevStep: 'Anterior',
    step1: 'Información',
    step2: 'Servicios',
    step3: 'Resumen',
    dragDrop: 'Arrastra un archivo aquí o haz clic para seleccionar',
    supportedFormats: 'MP3, WAV, M4A, TXT (máx 25MB)',
    answeredQuestions: 'preguntas respondidas',
    emptyAnswer: 'Sin responder',
    transcription: 'Transcripción',
    recordings: 'Grabaciones Guardadas',
    noRecordings: 'No hay grabaciones aún',
    deleteRecording: 'Eliminar grabación',
    reanalyze: 'Re-analizar',
    categories: {
      context: 'Contexto del Negocio',
      needs: 'Necesidades y Objetivos',
      logistics: 'Logística',
      preferences: 'Preferencias',
    }
  },
  en: {
    title: 'Build Your Custom Package',
    subtitle: 'Tell me about your business and I\'ll create the perfect package for you',
    uploadAudio: 'Upload Audio/Transcript',
    uploadHint: 'Upload an audio file or text with your answers and AI will process them automatically',
    recordAudio: 'Record Audio',
    recording: 'Recording...',
    stopRecording: 'Stop',
    startRecording: 'Start Recording',
    processing: 'Processing with AI...',
    transcribing: 'Transcribing audio...',
    analyzeButton: 'Analyze & Fill',
    questionsTitle: 'Needs Questionnaire',
    questionsHint: 'Answer the questions or upload/record audio to fill them automatically',
    recommendedServices: 'Recommended Services',
    basedOnAnswers: 'Based on your answers, these services are ideal for you:',
    selectedServices: 'Selected Services',
    totalInvestment: 'Estimated Total Investment',
    oneTime: 'One-time',
    monthly: 'Monthly',
    generatePrompt: 'Generate Master Prompt',
    promptTitle: 'Master Prompt for Building',
    promptHint: 'Use this prompt to start building your project',
    copyPrompt: 'Copy Prompt',
    copied: 'Copied!',
    downloadPrompt: 'Download',
    savePackage: 'Save Package',
    saved: 'Saved!',
    nextStep: 'Next Step',
    prevStep: 'Previous',
    step1: 'Information',
    step2: 'Services',
    step3: 'Summary',
    dragDrop: 'Drag a file here or click to select',
    supportedFormats: 'MP3, WAV, M4A, TXT (max 25MB)',
    answeredQuestions: 'questions answered',
    emptyAnswer: 'Not answered',
    transcription: 'Transcription',
    recordings: 'Saved Recordings',
    noRecordings: 'No recordings yet',
    deleteRecording: 'Delete recording',
    reanalyze: 'Re-analyze',
    categories: {
      context: 'Business Context',
      needs: 'Needs & Goals',
      logistics: 'Logistics',
      preferences: 'Preferences',
    }
  }
};

// Question Card Component
const QuestionCard = ({ question, answer, onChange, isAnswered, language }) => {
  const CategoryIcon = CATEGORY_ICONS[question.category] || Target;
  const t = LABELS[language];
  
  return (
    <div className={`question-card ${isAnswered ? 'answered' : ''}`} data-testid={`question-${question.id}`}>
      <div className="question-header">
        <div className="question-category">
          <CategoryIcon size={14} />
          <span>{t.categories[question.category]}</span>
        </div>
        {isAnswered && <CheckCircle2 size={16} className="answered-icon" />}
      </div>
      <label className="question-label">{question.question}</label>
      {question.multiline ? (
        <textarea
          value={answer || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          placeholder={question.placeholder}
          rows={3}
        />
      ) : (
        <input
          type="text"
          value={answer || ''}
          onChange={(e) => onChange(question.id, e.target.value)}
          placeholder={question.placeholder}
        />
      )}
    </div>
  );
};

// Service Selection Card
const ServiceCard = ({ service, isSelected, onToggle, language }) => {
  const priceLabel = service.perMonth 
    ? `$${service.basePrice}/${language === 'es' ? 'mes' : 'mo'}` 
    : service.perHour 
    ? `$${service.basePrice}/${language === 'es' ? 'hora' : 'hr'}`
    : `$${service.basePrice.toLocaleString()}`;

  return (
    <div 
      className={`service-select-card ${isSelected ? 'selected' : ''}`}
      onClick={() => onToggle(service.id)}
      data-testid={`service-select-${service.id}`}
    >
      <div className="service-select-check">
        {isSelected ? <Check size={16} /> : null}
      </div>
      <div className="service-select-content">
        <h4>{service.name}</h4>
        <p>{service.description}</p>
      </div>
      <div className="service-select-price" style={{ color: service.categoryColor }}>
        {priceLabel}
      </div>
    </div>
  );
};

// Recording Item Component
const RecordingItem = ({ recording, onDelete, onUse, language }) => {
  const t = LABELS[language];
  const date = new Date(recording.created_at).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });

  return (
    <div className="recording-item" data-testid={`recording-${recording.id}`}>
      <div className="recording-info">
        <Mic size={16} />
        <div>
          <span className="recording-name">{recording.original_filename}</span>
          <span className="recording-date">{date}</span>
        </div>
      </div>
      <div className="recording-actions">
        {recording.transcription && (
          <button 
            className="btn btn-sm btn-outline"
            onClick={() => onUse(recording.transcription)}
            title={t.reanalyze}
          >
            <RefreshCw size={14} />
          </button>
        )}
        <button 
          className="btn btn-sm btn-danger-outline"
          onClick={() => onDelete(recording.id)}
          title={t.deleteRecording}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

// Main Package Builder Component for Lead Profile
const AIPackageBuilder = ({ leadId, leadName, currentStage = 'new', onSave }) => {
  const { language } = useLanguage();
  const t = LABELS[language];
  const questions = CLIENT_QUESTIONS[language];
  const allServices = getAllServices(language);
  const catalog = SERVICES_CATALOG[language];
  
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [selectedServices, setSelectedServices] = useState([]);
  const [recommendedServices, setRecommendedServices] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [masterPrompt, setMasterPrompt] = useState('');
  const [promptCopied, setPromptCopied] = useState(false);
  const [packageSaved, setPackageSaved] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [transcription, setTranscription] = useState('');
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordings, setRecordings] = useState([]);
  
  const answeredCount = Object.values(answers).filter(a => a && a.trim()).length;
  const totalQuestions = questions.length;
  const pricing = calculatePackagePrice(selectedServices, language);

  // Load existing package and recordings when component mounts
  useEffect(() => {
    const loadPackage = async () => {
      try {
        const response = await apiRequest(`/api/package-builder/package/${leadId}?stage=${currentStage}`);
        if (response.package) {
          setAnswers(response.package.answers || {});
          setSelectedServices(response.package.selected_services || []);
          if (response.package.master_prompt) {
            setMasterPrompt(response.package.master_prompt);
          }
        }
      } catch (error) {
        console.log('No existing package found');
      }
    };
    
    const loadRecs = async () => {
      try {
        const response = await apiRequest(`/api/package-builder/recordings/${leadId}`);
        setRecordings(response.recordings || []);
      } catch (error) {
        console.log('Error loading recordings:', error);
      }
    };
    
    if (leadId) {
      loadPackage();
      loadRecs();
    }
  }, [leadId, currentStage]);

  // Recording timer
  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const toggleService = useCallback((serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  }, []);

  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  }, []);

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setUploadedFile(file);
    }
  }, []);

  // Start recording audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Save the recording
        await saveRecording(audioBlob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error starting recording:', error);
      alert(language === 'es' 
        ? 'No se pudo acceder al micrófono. Por favor permite el acceso.' 
        : 'Could not access microphone. Please allow access.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Save recording to server
  const saveRecording = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, `recording_${Date.now()}.webm`);
      formData.append('lead_id', leadId);
      formData.append('stage', currentStage);

      const response = await apiRequest('/api/package-builder/save-recording', {
        method: 'POST',
        body: formData,
      });

      if (response.success) {
        setRecordings(prev => [response.recording, ...prev]);
        
        // If transcription was successful, analyze it
        if (response.recording.transcription) {
          setTranscription(response.recording.transcription);
          await analyzeText(response.recording.transcription);
        }
      }
    } catch (error) {
      console.error('Error saving recording:', error);
    }
    setIsProcessing(false);
  };

  // Delete recording
  const deleteRecording = async (recordingId) => {
    try {
      await apiRequest(`/api/package-builder/recording/${recordingId}`, {
        method: 'DELETE',
      });
      setRecordings(prev => prev.filter(r => r.id !== recordingId));
    } catch (error) {
      console.error('Error deleting recording:', error);
    }
  };

  // Use transcription from existing recording
  const useTranscription = async (text) => {
    setTranscription(text);
    await analyzeText(text);
  };

  // Analyze text (from transcription or file)
  const analyzeText = async (text) => {
    setIsProcessing(true);
    try {
      // Create a text blob for the API
      const textBlob = new Blob([text], { type: 'text/plain' });
      const formData = new FormData();
      formData.append('file', textBlob, 'transcription.txt');
      formData.append('language', language);
      formData.append('questions', JSON.stringify(questions.map(q => ({ id: q.id, question: q.question }))));

      const response = await apiRequest('/api/package-builder/analyze', {
        method: 'POST',
        body: formData,
      });

      if (response.answers) {
        setAnswers(prev => ({ ...prev, ...response.answers }));
      }
      if (response.recommended_services) {
        setRecommendedServices(response.recommended_services);
        setSelectedServices(prev => [...new Set([...prev, ...response.recommended_services])]);
      }
      if (response.analysis) {
        setAiAnalysis(response.analysis);
      }
    } catch (error) {
      console.error('Error analyzing text:', error);
    }
    setIsProcessing(false);
  };

  const processWithAI = useCallback(async () => {
    if (!uploadedFile) return;
    
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('language', language);
      formData.append('questions', JSON.stringify(questions.map(q => ({ id: q.id, question: q.question }))));

      const response = await apiRequest('/api/package-builder/analyze', {
        method: 'POST',
        body: formData,
      });

      if (response.answers) {
        setAnswers(prev => ({ ...prev, ...response.answers }));
      }
      if (response.recommended_services) {
        setRecommendedServices(response.recommended_services);
        setSelectedServices(prev => [...new Set([...prev, ...response.recommended_services])]);
      }
      if (response.analysis) {
        setAiAnalysis(response.analysis);
      }
      if (response.transcription) {
        setTranscription(response.transcription);
      }
    } catch (error) {
      console.error('Error processing file:', error);
    }
    setIsProcessing(false);
  }, [uploadedFile, language, questions]);

  const generateMasterPrompt = useCallback(() => {
    const selectedServiceDetails = allServices.filter(s => selectedServices.includes(s.id));
    
    const prompt = language === 'es' 
      ? `# Proyecto de Desarrollo para ${leadName || 'Cliente'}

## Contexto del Cliente
- **Tipo de negocio**: ${answers.business_type || 'No especificado'}
- **Tamaño**: ${answers.business_size || 'No especificado'}
- **Herramientas actuales**: ${answers.current_tech || 'No especificado'}

## Problema Principal
${answers.main_problem || 'No especificado'}

## Objetivos
${answers.goals || 'No especificado'}

## Requerimientos Específicos
${answers.specific_features || 'No especificado'}

## Timeline y Presupuesto
- **Timeline**: ${answers.timeline || 'No especificado'}
- **Presupuesto**: ${answers.budget_range || 'No especificado'}

## Prioridades del Cliente
${answers.priorities || 'No especificado'}

## Intentos Previos
${answers.previous_attempts || 'No especificado'}

## Servicios a Implementar
${selectedServiceDetails.map(s => `- **${s.name}** (${s.categoryName}): ${s.description}`).join('\n')}

## Inversión Estimada
- **Único**: $${pricing.oneTimeTotal.toLocaleString()}
- **Mensual**: $${pricing.monthlyTotal.toLocaleString()}

---

## Instrucciones de Implementación

1. **Fase 1 - Setup Inicial**
   - Configurar el entorno de desarrollo
   - Establecer la arquitectura base
   - Implementar autenticación si es necesario

2. **Fase 2 - Funcionalidades Core**
   ${selectedServiceDetails.slice(0, 3).map(s => `- Implementar ${s.name}`).join('\n   ')}

3. **Fase 3 - Integraciones y Automatizaciones**
   ${selectedServiceDetails.slice(3).map(s => `- Configurar ${s.name}`).join('\n   ')}

4. **Fase 4 - Testing y Optimización**
   - Pruebas de funcionalidad
   - Optimización de rendimiento
   - Documentación

5. **Fase 5 - Despliegue y Entrega**
   - Deploy a producción
   - Capacitación al cliente
   - Entrega de documentación

---

Genera el código y la estructura necesaria para este proyecto. Prioriza ${answers.priorities || 'calidad y funcionalidad'}.`
      : `# Development Project for ${leadName || 'Client'}

## Client Context
- **Business type**: ${answers.business_type || 'Not specified'}
- **Size**: ${answers.business_size || 'Not specified'}
- **Current tools**: ${answers.current_tech || 'Not specified'}

## Main Problem
${answers.main_problem || 'Not specified'}

## Goals
${answers.goals || 'Not specified'}

## Specific Requirements
${answers.specific_features || 'Not specified'}

## Timeline and Budget
- **Timeline**: ${answers.timeline || 'Not specified'}
- **Budget**: ${answers.budget_range || 'Not specified'}

## Client Priorities
${answers.priorities || 'Not specified'}

## Previous Attempts
${answers.previous_attempts || 'Not specified'}

## Services to Implement
${selectedServiceDetails.map(s => `- **${s.name}** (${s.categoryName}): ${s.description}`).join('\n')}

## Estimated Investment
- **One-time**: $${pricing.oneTimeTotal.toLocaleString()}
- **Monthly**: $${pricing.monthlyTotal.toLocaleString()}

---

## Implementation Instructions

1. **Phase 1 - Initial Setup**
   - Set up development environment
   - Establish base architecture
   - Implement authentication if needed

2. **Phase 2 - Core Features**
   ${selectedServiceDetails.slice(0, 3).map(s => `- Implement ${s.name}`).join('\n   ')}

3. **Phase 3 - Integrations and Automations**
   ${selectedServiceDetails.slice(3).map(s => `- Configure ${s.name}`).join('\n   ')}

4. **Phase 4 - Testing and Optimization**
   - Functionality testing
   - Performance optimization
   - Documentation

5. **Phase 5 - Deployment and Delivery**
   - Deploy to production
   - Client training
   - Documentation delivery

---

Generate the code and structure needed for this project. Prioritize ${answers.priorities || 'quality and functionality'}.`;

    setMasterPrompt(prompt);
    setStep(3);
  }, [answers, selectedServices, allServices, pricing, language, leadName]);

  const copyPrompt = useCallback(() => {
    navigator.clipboard.writeText(masterPrompt);
    setPromptCopied(true);
    setTimeout(() => setPromptCopied(false), 2000);
  }, [masterPrompt]);

  const downloadPrompt = useCallback(() => {
    const blob = new Blob([masterPrompt], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${leadName || 'project'}-prompt-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, [masterPrompt, leadName]);

  const savePackage = async () => {
    if (!leadId) return;
    
    try {
      const formData = new FormData();
      formData.append('lead_id', leadId);
      formData.append('answers', JSON.stringify(answers));
      formData.append('selected_services', JSON.stringify(selectedServices));
      formData.append('master_prompt', masterPrompt);
      formData.append('pricing', JSON.stringify(pricing));
      formData.append('stage', currentStage);

      await apiRequest('/api/package-builder/save-package', {
        method: 'POST',
        body: formData,
      });

      setPackageSaved(true);
      setTimeout(() => setPackageSaved(false), 2000);
      
      if (onSave) {
        onSave({ answers, selectedServices, masterPrompt, pricing });
      }
    } catch (error) {
      console.error('Error saving package:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderStep1 = () => (
    <div className="builder-step step-1">
      {/* Audio Recording Section */}
      <div className="recording-section">
        <h3><Mic size={20} /> {t.recordAudio}</h3>
        
        <div className="recording-controls">
          {isRecording ? (
            <button 
              className="btn btn-danger recording-btn"
              onClick={stopRecording}
            >
              <Square size={18} />
              {t.stopRecording} ({formatTime(recordingTime)})
            </button>
          ) : (
            <button 
              className="btn btn-primary recording-btn"
              onClick={startRecording}
              disabled={isProcessing}
            >
              <Mic size={18} />
              {t.startRecording}
            </button>
          )}
          
          {isRecording && (
            <div className="recording-indicator">
              <span className="pulse-dot" />
              {t.recording}
            </div>
          )}
        </div>

        {/* Saved Recordings */}
        {recordings.length > 0 && (
          <div className="recordings-list">
            <h4>{t.recordings}</h4>
            {recordings.map(recording => (
              <RecordingItem
                key={recording.id}
                recording={recording}
                onDelete={deleteRecording}
                onUse={useTranscription}
                language={language}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div className="upload-section">
        <h3><Upload size={20} /> {t.uploadAudio}</h3>
        <p>{t.uploadHint}</p>
        
        <div 
          className={`upload-dropzone ${uploadedFile ? 'has-file' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.m4a,.txt,.webm,.ogg"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
          
          {uploadedFile ? (
            <div className="uploaded-file-info">
              <FileText size={24} />
              <span>{uploadedFile.name}</span>
              <button onClick={(e) => { e.stopPropagation(); setUploadedFile(null); }}>
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <Upload size={32} />
              <p>{t.dragDrop}</p>
              <span className="upload-formats">{t.supportedFormats}</span>
            </>
          )}
        </div>

        {uploadedFile && (
          <button 
            className="btn btn-primary analyze-btn"
            onClick={processWithAI}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <><Loader2 size={18} className="spinning" /> {t.processing}</>
            ) : (
              <><Brain size={18} /> {t.analyzeButton}</>
            )}
          </button>
        )}
      </div>

      {/* Transcription Display */}
      {transcription && (
        <div className="transcription-box">
          <h4><FileText size={18} /> {t.transcription}</h4>
          <p>{transcription}</p>
        </div>
      )}

      {/* Questions Section */}
      <div className="questions-section">
        <div className="questions-header">
          <h3>{t.questionsTitle}</h3>
          <span className="questions-progress">
            {answeredCount}/{totalQuestions} {t.answeredQuestions}
          </span>
        </div>
        <p className="questions-hint">{t.questionsHint}</p>
        
        <div className="questions-grid">
          {questions.map(question => (
            <QuestionCard
              key={question.id}
              question={question}
              answer={answers[question.id]}
              onChange={handleAnswerChange}
              isAnswered={!!(answers[question.id] && answers[question.id].trim())}
              language={language}
            />
          ))}
        </div>
      </div>

      {/* AI Analysis Result */}
      {aiAnalysis && (
        <div className="ai-analysis-box">
          <h4><Sparkles size={18} /> AI Analysis</h4>
          <p>{aiAnalysis}</p>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="builder-step step-2">
      {/* Recommended Services */}
      {recommendedServices.length > 0 && (
        <div className="recommended-section">
          <h3><Sparkles size={20} /> {t.recommendedServices}</h3>
          <p>{t.basedOnAnswers}</p>
          <div className="recommended-grid">
            {allServices
              .filter(s => recommendedServices.includes(s.id))
              .map(service => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  isSelected={selectedServices.includes(service.id)}
                  onToggle={toggleService}
                  language={language}
                />
              ))}
          </div>
        </div>
      )}

      {/* All Services by Category */}
      <div className="services-selection-section">
        <h3>{t.selectedServices}</h3>
        
        {catalog.categories.map(category => (
          <div key={category.id} className="service-category-accordion">
            <button 
              className="category-accordion-header"
              onClick={() => toggleCategory(category.id)}
              style={{ '--cat-color': category.color }}
            >
              <div className="category-header-left">
                <span className="category-dot" style={{ backgroundColor: category.color }} />
                <span>{category.name}</span>
                <span className="category-selected-count">
                  {category.services.filter(s => selectedServices.includes(s.id)).length}/{category.services.length}
                </span>
              </div>
              {expandedCategories[category.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            
            {expandedCategories[category.id] && (
              <div className="category-services-list">
                {category.services.map(service => (
                  <ServiceCard
                    key={service.id}
                    service={{ ...service, categoryColor: category.color }}
                    isSelected={selectedServices.includes(service.id)}
                    onToggle={toggleService}
                    language={language}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pricing Summary */}
      <div className="pricing-summary-card">
        <h4><DollarSign size={20} /> {t.totalInvestment}</h4>
        <div className="pricing-breakdown">
          <div className="price-row">
            <span>{t.oneTime}</span>
            <span className="price-value">${pricing.oneTimeTotal.toLocaleString()}</span>
          </div>
          {pricing.monthlyTotal > 0 && (
            <div className="price-row monthly">
              <span>{t.monthly}</span>
              <span className="price-value">${pricing.monthlyTotal.toLocaleString()}/mo</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="builder-step step-3">
      <div className="prompt-section">
        <div className="prompt-header">
          <h3><Rocket size={20} /> {t.promptTitle}</h3>
          <p>{t.promptHint}</p>
        </div>
        
        <div className="prompt-actions">
          <button className="btn btn-primary" onClick={copyPrompt}>
            {promptCopied ? <><CheckCircle2 size={18} /> {t.copied}</> : <><Copy size={18} /> {t.copyPrompt}</>}
          </button>
          <button className="btn btn-outline" onClick={downloadPrompt}>
            <Download size={18} /> {t.downloadPrompt}
          </button>
          {leadId && (
            <button className="btn btn-success" onClick={savePackage}>
              {packageSaved ? <><CheckCircle2 size={18} /> {t.saved}</> : <><Save size={18} /> {t.savePackage}</>}
            </button>
          )}
        </div>
        
        <div className="prompt-preview">
          <pre>{masterPrompt}</pre>
        </div>
      </div>
    </div>
  );

  return (
    <div className="package-builder-container" data-testid="ai-package-builder">
      <div className="builder-header">
        <h2><Package size={24} /> {t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      {/* Steps Indicator */}
      <div className="builder-steps">
        <div className={`builder-step-indicator ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">{t.step1}</span>
        </div>
        <div className="step-connector" />
        <div className={`builder-step-indicator ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">{t.step2}</span>
        </div>
        <div className="step-connector" />
        <div className={`builder-step-indicator ${step >= 3 ? 'active' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">{t.step3}</span>
        </div>
      </div>

      {/* Processing Indicator */}
      {isProcessing && (
        <div className="processing-overlay">
          <Loader2 size={32} className="spinning" />
          <span>{t.processing}</span>
        </div>
      )}

      {/* Step Content */}
      <div className="builder-content">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>

      {/* Navigation */}
      <div className="builder-navigation">
        {step > 1 && (
          <button className="btn btn-outline" onClick={() => setStep(step - 1)}>
            {t.prevStep}
          </button>
        )}
        {step === 1 && (
          <button 
            className="btn btn-primary" 
            onClick={() => setStep(2)}
            disabled={answeredCount < 2}
          >
            {t.nextStep} <ArrowRight size={18} />
          </button>
        )}
        {step === 2 && (
          <button 
            className="btn btn-primary" 
            onClick={generateMasterPrompt}
            disabled={selectedServices.length === 0}
          >
            <Zap size={18} /> {t.generatePrompt}
          </button>
        )}
      </div>
    </div>
  );
};

export default AIPackageBuilder;
